import React, { useMemo, useState, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import NtText from "../../../components/NtText";
import NtCodeInput from "../../../components/NtCodeInput";
import NtOnboardingEditorialSection from "../NtOnboardingEditorialSection";
import { useTheme } from "../../../context/ThemeContext";

/**
 * Final pre-signup step. The controller auto-fires `/onboarding/security-code/
 * request` when this step mounts (sending a 6-digit code to the admin email);
 * the user enters it via the shared NtCodeInput (same per-digit UX as the
 * ntvoiplib auth flow) and the "Complete signup" button validates locally
 * before submitting the signup payload.
 */
function NtOnboardingVerifyStep({
  email,
  code,
  onChangeCode,
  sending,
  resendDisabledUntil,
  onResend,
  lastError,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  // Tick once a second while the resend cooldown is active so the countdown
  // visibly decrements and flips to the active button when it hits zero. The
  // computed values below read `now` (not Date.now()) so they update on tick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!resendDisabledUntil) return;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= resendDisabledUntil) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [resendDisabledUntil]);

  const canResend = !sending && (!resendDisabledUntil || now >= resendDisabledUntil);
  const remainingSec = !canResend && resendDisabledUntil
    ? Math.max(0, Math.ceil((resendDisabledUntil - now) / 1000))
    : 0;

  return (
    <View>
      <NtOnboardingEditorialSection
        first
        label="Verify your email"
        caption={
          email
            ? `We sent a 6-digit code to ${email}. Enter it below to finish creating your account.`
            : "We sent a 6-digit code to your admin email."
        }
      >
        <NtCodeInput
          value={code || ""}
          onChange={(v) => onChangeCode && onChangeCode(v)}
          length={6}
          autoFocus
        />

        {!!lastError && <NtText style={styles.error}>{lastError}</NtText>}

        <View style={styles.resendRow}>
          <NtText style={styles.resendHint}>Didn't get it? Check spam, or</NtText>
          <Pressable
            onPress={canResend ? onResend : undefined}
            style={({ pressed }) => [
              styles.resendBtn,
              pressed && canResend && { opacity: 0.7 },
              !canResend && { opacity: 0.5 },
            ]}
          >
            <NtText style={[styles.resendBtnText, canResend ? styles.resendBtnTextActive : null]}>
              {sending
                ? "Sending…"
                : canResend
                ? "resend the code"
                : `resend in ${remainingSec}s`}
            </NtText>
          </Pressable>
        </View>
      </NtOnboardingEditorialSection>
    </View>
  );
}

const makeStyles = (theme) =>
  StyleSheet.create({
    error: {
      color: theme.red,
      fontSize: 12.5,
      marginTop: 8,
    },
    resendRow: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: 16,
      gap: 6,
      flexWrap: "wrap",
    },
    resendHint: { color: theme.textDim, fontSize: 12.5 },
    resendBtn: { paddingVertical: 2 },
    resendBtnText: { color: theme.textMute, fontSize: 12.5 },
    resendBtnTextActive: {
      color: theme.brand2,
      textDecorationLine: "underline",
      fontWeight: "600",
    },
  });

export default NtOnboardingVerifyStep;
